import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/config/firebase';

export interface ClientSummary {
  id: string;
  name: string;
  logoUrl?: string | null;
  logoPath?: string | null;
  logoFileName?: string | null;
}

export const clientsQueryKeys = {
  all: ['clients'] as const,
};

const clientsSnapshotManager: {
  unsub: (() => void) | null;
  listeners: number;
} = {
  unsub: null,
  listeners: 0,
};

interface UseClientsQueryResult {
  data: ClientSummary[];
  isLoading: boolean;
  error: Error | null;
}

export function useClientsQuery(): UseClientsQueryResult {
  const queryClient = useQueryClient();

  const queryResult = useQuery<ClientSummary[]>({
    queryKey: clientsQueryKeys.all,
    initialData: () =>
      (queryClient.getQueryData(clientsQueryKeys.all) as ClientSummary[] | undefined) ?? [],
    queryFn: async () => {
      const cached = queryClient.getQueryData(clientsQueryKeys.all) as
        | ClientSummary[]
        | undefined;
      if (cached && cached.length) return cached;

      const snapshot = await getDocs(query(collection(db, 'clients'), orderBy('name')));
      return snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            name: doc.data().name,
            logoUrl: doc.data().logoUrl ?? null,
            logoPath: doc.data().logoPath ?? null,
            logoFileName: doc.data().logoFileName ?? null,
          }) satisfies ClientSummary
      );
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    clientsSnapshotManager.listeners += 1;

    if (!clientsSnapshotManager.unsub) {
      const q = query(collection(db, 'clients'), orderBy('name'));
      clientsSnapshotManager.unsub = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              name: doc.data().name,
              logoUrl: doc.data().logoUrl ?? null,
              logoPath: doc.data().logoPath ?? null,
              logoFileName: doc.data().logoFileName ?? null,
            }) satisfies ClientSummary
        );
        queryClient.setQueryData(clientsQueryKeys.all, docs);
      });
    }

    return () => {
      clientsSnapshotManager.listeners -= 1;
      if (clientsSnapshotManager.listeners === 0 && clientsSnapshotManager.unsub) {
        clientsSnapshotManager.unsub();
        clientsSnapshotManager.unsub = null;
      }
    };
  }, [queryClient]);

  return {
    data: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    error: queryResult.error ?? null,
  };
}
